/**
 * GOKI Image - React Frontend
 *
 * Haupt-App-Komponente mit:
 * - TanStack Query Provider
 * - React Router
 * - Auth Provider
 * - Error Boundary
 * - Toast Notifications
 * - React.lazy Code-Splitting for non-core routes
 *
 * Tailwind CSS v4.1 only (Bootstrap removed)
 */

import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';

import { queryClient } from '@/lib/query-client';
import { SupabaseAuthProvider } from '@/contexts/SupabaseAuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';

// Core components (eagerly loaded - needed on most pages)
import { NavbarV2 } from '@/components/v2/Navbar';
import { BottomNavigation } from '@/components/v2/BottomNavigation';
import { OfflineBanner } from '@/components/v2/OfflineBanner';
import { MfaEnforcementBanner } from '@/components/v2/admin/MfaEnforcementBanner';
import { ReauthBanner } from '@/components/v2/reauth/ReauthBanner';
import { DashboardV2 } from '@/routes/v2/Dashboard';
import { SettingsV2 } from '@/routes/v2/Settings';
import { Credits as CreditsV2 } from '@/routes/v2/Credits';
// Rollback-Fallback: Route index → <MergedProductsPage /> (1 Zeile App.tsx) — wird nicht direkt gerendert
import { MergedProductsPage as _MergedProductsPage } from '@/routes/v2/MergedProductsPage';
import { ProductsV2 } from '@/routes/v2/Products';
import { IssuesPage } from '@/routes/v2/IssuesPage';
import { ProductDetailPageV2 } from '@/routes/v2/products/ProductDetailPage';
import { Categories as CategoriesV2 } from '@/routes/v2/products/Categories';
import { ToolsV2 } from '@/routes/v2/tools';
import { ProductsLayout } from '@/components/v2/products/layout';

// Auth (eagerly loaded - needed for login flow)
import {
  ResetPassword as ResetPasswordV2,
  Register as RegisterV2,
  VerifyEmailCallback,
} from '@/routes/v2/auth';
import { LoginRedesign } from '@/routes/v2/auth/LoginRedesign';

// Lazy-loaded routes (code-split for smaller initial bundle)
const PresetsLibraryV2 = lazy(() => import('@/routes/v2/presets-library').then(m => ({ default: m.PresetsLibrary })));
const TemplateCollections = lazy(() => import('@/routes/v2/template-collections').then(m => ({ default: m.TemplateCollections })));
const CollectionBuilder = lazy(() => import('@/routes/v2/template-collections').then(m => ({ default: m.CollectionBuilder })));
const TemplatesPage = lazy(() => import('@/components/v2/template-marketplace').then(m => ({ default: m.TemplatesPage })));
const MarketplacePage = lazy(() => import('@/components/v2/template-marketplace').then(m => ({ default: m.MarketplacePage })));
const AdminV2 = lazy(() => import('@/routes/v2/admin').then(m => ({ default: m.Admin })));
const WorkspaceSettingsV2 = lazy(() => import('@/routes/v2/workspace-settings').then(m => ({ default: m.WorkspaceSettings })));
const ImageSettingsPage = lazy(() => import('@/routes/v2/workspace-settings/ImageSettingsPage').then(m => ({ default: m.ImageSettingsPage })));
const BulletMappingPage = lazy(() => import('@/routes/v2/workspace-settings/BulletMappingPage').then(m => ({ default: m.BulletMappingPage })));
const Brands = lazy(() => import('@/routes/v2/workspace-settings/Brands').then(m => ({ default: m.Brands })));
const CreatorEarningsV2 = lazy(() => import('@/routes/v2/creator-earnings').then(m => ({ default: m.CreatorEarnings })));
const ApiTokensPage = lazy(() => import('@/components/v2/profile/api-tokens').then(m => ({ default: m.ApiTokensPage })));
const MfaEnrollmentPage = lazy(() => import('@/components/v2/profile/mfa').then(m => ({ default: m.MfaEnrollmentPage })));
const PartnersOverview = lazy(() => import('@/routes/v2/partners').then(m => ({ default: m.PartnersOverview })));
const PartnersRegister = lazy(() => import('@/routes/v2/partners').then(m => ({ default: m.PartnersRegister })));
const PartnersEarnings = lazy(() => import('@/routes/v2/partners').then(m => ({ default: m.PartnersEarnings })));
const PricingPage = lazy(() => import('@/routes/v2/subscription').then(m => ({ default: m.PricingPage })));
const CurrentPlanPage = lazy(() => import('@/routes/v2/subscription').then(m => ({ default: m.CurrentPlanPage })));
const RefLanding = lazy(() => import('@/routes/v2/ref').then(m => ({ default: m.RefLanding })));
const ReferralPage = lazy(() => import('@/routes/v2/referral').then(m => ({ default: m.ReferralPage })));
const ConverterLanding = lazy(() => import('@/components/public/converter').then(m => ({ default: m.ConverterLanding })));
const PublicAuditPage = lazy(() => import('@/components/v2/public/PublicAuditPage').then(m => ({ default: m.PublicAuditPage })));
const LandingPage = lazy(() => import('@/components/public/landing').then(m => ({ default: m.LandingPage })));
const ToolsHub = lazy(() => import('@/components/public/tools').then(m => ({ default: m.ToolsHub })));
const ImpressumPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.ImpressumPage })));
const DatenschutzPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.DatenschutzPage })));
const AGBPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.AGBPage })));
const FAQPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.FAQPage })));
const AUPPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.AUPPage })));
const SubprocessorsPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.SubprocessorsPage })));
const WiderrufPage = lazy(() => import('@/components/public/legal').then(m => ({ default: m.WiderrufPage })));
const CookieBanner = lazy(() => import('@/components/consent').then(m => ({ default: m.CookieBanner })));

/** Legacy redirect: /v2/workspace/* → /v2/tools/* preserving subpath */
function WorkspaceToToolsRedirect() {
  const { '*': splat } = useParams();
  return <Navigate to={`/v2/tools/${splat || ''}`} replace />;
}

// Styles (Tailwind only)
import '@/styles/v2/globals.css';
import '@/styles/v2/dark.css';

/**
 * Loading fallback for lazy-loaded routes
 */
function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-neutral-100" />
    </div>
  );
}

/**
 * V2 Layout - Tailwind-basiert
 * Background/Foreground wird über globals.css auf body gesetzt
 */
function V2Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100">
      <NavbarV2 />
      <OfflineBanner />
      {/* Globales Re-Auth-Banner: sticky, no-op falls keine Integration neu autorisiert werden muss */}
      <ReauthBanner />
      <main className="pt-16 pb-16 lg:pb-0">
        {/* Globales Grace-Period-Banner: in-flow, no-op für Nicht-Admins/enrolled/loading */}
        <MfaEnforcementBanner />
        {children}
      </main>
      <BottomNavigation />
    </div>
  );
}

/**
 * Product Detail Redirect - preserves productId parameter
 */
function ProductDetailRedirect() {
  const { productId } = useParams();
  return <Navigate to={`/v2/products/${productId}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
          <Suspense fallback={null}>
            <CookieBanner />
          </Suspense>
          <Routes>
            {/* Public audit share page - no auth required */}
            <Route path="/public/audit/:shareToken" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <PublicAuditPage />
              </Suspense>
            } />

            {/* Public landing page - no auth required */}
            <Route path="/" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <LandingPage />
              </Suspense>
            } />
            <Route path="/tools" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <ToolsHub />
              </Suspense>
            } />

            {/* Legal pages - public, no auth required */}
            <Route path="/impressum" element={<Suspense fallback={<RouteLoadingFallback />}><ImpressumPage /></Suspense>} />
            <Route path="/datenschutz" element={<Suspense fallback={<RouteLoadingFallback />}><DatenschutzPage /></Suspense>} />
            <Route path="/agb" element={<Suspense fallback={<RouteLoadingFallback />}><AGBPage /></Suspense>} />
            <Route path="/faq" element={<Suspense fallback={<RouteLoadingFallback />}><FAQPage /></Suspense>} />
            <Route path="/aup" element={<Suspense fallback={<RouteLoadingFallback />}><AUPPage /></Suspense>} />
            <Route path="/subprocessors" element={<Suspense fallback={<RouteLoadingFallback />}><SubprocessorsPage /></Suspense>} />
            <Route path="/widerruf" element={<Suspense fallback={<RouteLoadingFallback />}><WiderrufPage /></Suspense>} />

            {/* Alle Routes MIT Auth-Provider */}
            <Route path="/*" element={
              <SupabaseAuthProvider>
                  <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    {/* ============================================
                        V2 ROUTES - Tailwind-basiert
                    ============================================ */}

                    {/* V2 Dashboard */}
                    <Route
                      path="/v2/dashboard"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <DashboardV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Settings */}
                    <Route
                      path="/v2/settings"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <SettingsV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Credits */}
                    <Route
                      path="/v2/credits"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <CreditsV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Products - with Sidebar Layout */}
                    <Route
                      path="/v2/products"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <ProductsLayout />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    >
                      {/* Products List — S6: Redesign-Shell mit Plan-012-Parität */}
                      <Route index element={<ProductsV2 />} />

                      {/* Brands */}
                      <Route path="brands" element={<Brands />} />

                      {/* Categories */}
                      <Route path="categories" element={<CategoriesV2 />} />

                      {/* Issues — Subsystem 3: Workspace-weite Issue-Übersicht */}
                      <Route path="issues" element={<IssuesPage />} />

                      {/* Channel Readiness — Plan 012: Merged into /v2/products */}
                      <Route path="channel-readiness" element={<Navigate to="/v2/products" replace />} />
                    </Route>

                    {/* V2 Product Detail (no sidebar) */}
                    <Route
                      path="/v2/products/:productId"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <ProductDetailPageV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Tools (formerly Workspace) */}
                    <Route
                      path="/v2/tools/*"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <ToolsV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Legacy redirect: /v2/workspace/* -> /v2/tools/* (preserves subpath) */}
                    <Route path="/v2/workspace/*" element={<WorkspaceToToolsRedirect />} />

                    {/* V2 Login - Modern Split-Screen */}
                    <Route path="/v2/login" element={<LoginRedesign />} />

                    {/* V2 Register (Tailwind) */}
                    <Route path="/v2/register" element={<RegisterV2 />} />

                    {/* V2 Reset Password (Tailwind) */}
                    <Route path="/v2/reset-password" element={<ResetPasswordV2 />} />

                    {/* V2 Email Verification Callback (Public) */}
                    <Route path="/v2/verify-email-callback" element={<VerifyEmailCallback />} />

                    {/* V2 Referral Landing (Public - no auth required) */}
                    <Route path="/ref/:referralUuid" element={<RefLanding />} />

                    {/* Public Image Converter (no auth required) */}
                    <Route path="/convert/:formats" element={<ConverterLanding />} />
                    <Route path="/convert" element={<ConverterLanding />} />

                    {/* V2 Custom Presets - Redirect to Templates */}
                    <Route
                      path="/v2/custom-presets"
                      element={<Navigate to="/v2/templates" replace />}
                    />

                    {/* V2 Presets Library (Tailwind) */}
                    <Route
                      path="/v2/presets-library"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <PresetsLibraryV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Templates Management (Tailwind) */}
                    <Route
                      path="/v2/templates"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <TemplatesPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Marketplace Discovery (Tailwind) */}
                    <Route
                      path="/v2/marketplace"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <MarketplacePage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Template Collections (Tailwind) */}
                    <Route
                      path="/v2/template-collections"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <TemplateCollections />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Collection Builder (Tailwind) */}
                    <Route
                      path="/v2/template-collections/:collectionId"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <CollectionBuilder />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Admin (Tailwind) */}
                    <Route
                      path="/v2/admin/*"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <AdminV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Workspace Settings (Tailwind) */}
                    <Route
                      path="/v2/workspace-settings"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <WorkspaceSettingsV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Image Editor Settings (Tailwind) */}
                    <Route
                      path="/v2/workspace-settings/image-settings"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <ImageSettingsPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Bullet-Mapping Configuration (Remake → Apply, PR2 #10) */}
                    <Route
                      path="/v2/workspace-settings/integrations/:integrationId/bullet-mapping"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <BulletMappingPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Brands Management (Tailwind) */}
                    <Route
                      path="/v2/workspace-settings/brands"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <Brands />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Unified Image Editor - Now handled by Tools routes */}
                    {/* Redirect old assetId route to new unified tool */}
                    <Route
                      path="/v2/tools/image-editor/:assetId"
                      element={<Navigate to="/v2/tools/image-editor" replace />}
                    />

                    {/* V2 Creator Earnings (Tailwind) - DEPRECATED */}
                    <Route
                      path="/v2/creator/earnings"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <CreatorEarningsV2 />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* API Tokens Profile Page */}
                    <Route
                      path="/api-tokens"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <ApiTokensPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* MFA Enrollment Profile Page */}
                    <Route
                      path="/v2/profile/mfa"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <MfaEnrollmentPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Partners Program */}
                    <Route
                      path="/v2/partners"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <PartnersOverview />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/v2/partners/earnings"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <PartnersEarnings />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/v2/partners/register/:type"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <PartnersRegister />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Earnings redirect to Partners */}
                    <Route path="/v2/earnings" element={<Navigate to="/v2/partners/earnings" replace />} />

                    {/* V2 Pricing (Public - no auth required) */}
                    <Route
                      path="/v2/pricing"
                      element={
                        <V2Layout>
                          <PricingPage />
                        </V2Layout>
                      }
                    />

                    {/* V2 Subscription (Protected - current plan) */}
                    <Route
                      path="/v2/subscription"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <CurrentPlanPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Referral Page */}
                    <Route
                      path="/v2/referral"
                      element={
                        <ProtectedRoute>
                          <V2Layout>
                            <ReferralPage />
                          </V2Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* V2 Price Calculator - Redirect to Tools */}
                    <Route
                      path="/v2/price-calculator"
                      element={<Navigate to="/v2/tools/price-calculator" replace />}
                    />

                    {/* ============================================
                        LEGACY REDIRECTS - V1 -> V2
                        Backwards compatibility for old URLs
                    ============================================ */}

                    <Route path="/dashboard" element={<Navigate to="/v2/dashboard" replace />} />
                    <Route path="/workspace/*" element={<WorkspaceToToolsRedirect />} />
                    <Route path="/credits" element={<Navigate to="/v2/credits" replace />} />
                    <Route path="/custom-presets" element={<Navigate to="/v2/custom-presets" replace />} />
                    <Route path="/presets-library" element={<Navigate to="/v2/presets-library" replace />} />
                    <Route path="/settings" element={<Navigate to="/v2/settings" replace />} />
                    <Route path="/workspace-settings" element={<Navigate to="/v2/workspace-settings" replace />} />
                    <Route path="/creator/earnings" element={<Navigate to="/v2/creator/earnings" replace />} />
                    <Route path="/products" element={<Navigate to="/v2/products" replace />} />
                    <Route path="/products/:productId" element={<ProductDetailRedirect />} />
                    <Route path="/admin/*" element={<Navigate to="/v2/admin" replace />} />
                    <Route path="/reset-password" element={<Navigate to="/v2/reset-password" replace />} />

                    {/* Login redirects */}
                    <Route path="/login" element={<Navigate to="/v2/login" replace />} />
                    <Route path="/loginv2" element={<Navigate to="/v2/login" replace />} />
                    <Route path="/goecom-login" element={<Navigate to="/v2/login" replace />} />

                    {/* Default redirect */}
                    <Route path="*" element={<Navigate to="/v2/dashboard" replace />} />
                  </Routes>
                  </Suspense>
                </SupabaseAuthProvider>
            } />
          </Routes>
        </BrowserRouter>

        {/* Toast Notifications (Sonner) */}
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            duration: 4000,
            className: 'font-sans',
          }}
        />

        {/* React Query DevTools (nur in Development) */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
